import React, { useId } from 'react';
import { Clock, LogOut } from 'lucide-react';

/**
 * ============================================================================
 * ÉCRAN DE PASSAGE — l'école au soleil, à l'arrivée et au départ
 * ============================================================================
 *
 * Ce fichier existe à l'identique dans KLASYS (src/components/connexion/) et
 * dans le CIK (frontend/src/components/ecrans/), où il sert d'aperçu. Modifier
 * l'un, c'est modifier l'autre.
 *
 *   * `sens="arrivee"` : l'école s'éveille. Le ciel passe de l'aube au jour (au
 *     couchant le soir), le soleil se lève, le paysage monte, les fenêtres
 *     s'allument une à une, le texte apparaît.
 *   * `sens="depart"` : l'école s'endort. La scène est déjà là ; le soleil
 *     descend, le ciel se couche (passe à la nuit le soir, avec les étoiles),
 *     les élèves s'en vont, les fenêtres s'éteignent, le texte s'efface.
 *
 * Tout est dessiné et animé en CSS dans ce fichier : pas d'image à télécharger,
 * pas de dépendance d'animation. Les animations partent au montage ; pour les
 * rejouer, changer la `key` du composant.
 *
 * `apercu` : l'écran tient dans son cadre (16/9) au lieu de couvrir la fenêtre.
 * Les tailles de texte suivent la largeur du cadre (unités `cqw`), si bien que
 * l'aperçu du CIK est une réduction fidèle de l'écran réel.
 */

const CSS = `
.ep { container-type: inline-size; overflow: hidden; background: #1b4f6d; display: flex; align-items: center; justify-content: center; color: #fff; }
.ep-plein { position: fixed; inset: 0; z-index: 9999; }
.ep-apercu { position: relative; width: 100%; aspect-ratio: 16 / 9; border-radius: 12px; }
.ep-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.ep .ep-rayons, .ep .ep-halo { transform-box: fill-box; transform-origin: center; }
.ep .ep-rayons { animation: ep-tourne 40s linear infinite; }
.ep .ep-halo { animation: ep-respire 3.5s ease-in-out infinite; }
.ep .ep-nuage-1 { animation: ep-derive 38s linear infinite; }
.ep .ep-nuage-2 { animation: ep-derive 55s linear -20s infinite; }
.ep .ep-nuage-3 { animation: ep-derive 46s linear -35s infinite; }
.ep .ep-oiseaux { opacity: 0; animation: ep-vole 14s linear 1.2s infinite; }
.ep-depart .ep-oiseaux { animation-delay: .2s; }
.ep .ep-aile { animation: ep-bat .5s ease-in-out infinite alternate; transform-box: fill-box; transform-origin: center; }
.ep .ep-arbre { transform-box: fill-box; transform-origin: 50% 100%; animation: ep-balance 4s ease-in-out 1.4s infinite alternate; }
.ep .ep-drapeau { transform-box: fill-box; transform-origin: 0 50%; animation: ep-flotte 1.2s ease-in-out infinite alternate; }
.ep .ep-eleve { transform-box: fill-box; transform-origin: 50% 100%; }
.ep .ep-soleil, .ep .ep-monte { transform-box: view-box; }
.ep .ep-etoiles { opacity: 0; }
.ep-soir .ep-soleil-coeur, .ep-soir .ep-halo { fill: #f59e5b; }
.ep-soir .ep-rayons line { stroke: #f7b27a; }

/* ---- Arrivée ---- */
.ep-arrivee .ep-ciel-fin { opacity: 0; animation: ep-apparait 2.4s ease-in-out .2s forwards; }
.ep-arrivee .ep-soleil { transform: translateY(260px); animation: ep-leve 2.2s cubic-bezier(.2,.8,.2,1) forwards; }
.ep-arrivee.ep-soir .ep-soleil { animation-name: ep-leve-soir; }
.ep-arrivee .ep-monte { opacity: 0; transform: translateY(60px); animation: ep-arrive .9s cubic-bezier(.2,.8,.2,1) forwards; }
.ep-arrivee .ep-d1 { animation-delay: .15s; }
.ep-arrivee .ep-d2 { animation-delay: .35s; }
.ep-arrivee .ep-d3 { animation-delay: .55s; }
.ep-arrivee .ep-d4 { animation-delay: .8s; }
.ep-arrivee .ep-eleve { animation: ep-sautille 1.6s ease-in-out 1.3s infinite; }
.ep-arrivee .ep-eleve-2 { animation-delay: 1.5s; }
.ep-arrivee .ep-eleve-3 { animation-delay: 1.7s; }
.ep-arrivee .ep-fenetre { fill: #3084b5; animation: ep-allume .5s ease-out forwards; }
.ep-arrivee.ep-soir .ep-fenetre { animation-name: ep-allume-soir; }
.ep-arrivee .ep-f1 { animation-delay: 1.1s; }
.ep-arrivee .ep-f2 { animation-delay: 1.35s; }
.ep-arrivee .ep-f3 { animation-delay: 1.6s; }
.ep-arrivee .ep-f4 { animation-delay: 1.85s; }

/* ---- Départ ---- */
.ep-depart .ep-ciel-fin { opacity: 0; animation: ep-apparait 2.2s ease-in-out .1s forwards; }
.ep-depart .ep-soleil { transform: translateY(0); animation: ep-descend 2.3s cubic-bezier(.5,0,.75,.4) forwards; }
.ep-depart.ep-soir .ep-soleil { transform: translateY(150px); }
.ep-depart .ep-fenetre { fill: #ffd976; animation: ep-eteint .45s ease-in forwards; }
.ep-depart.ep-soir .ep-fenetre { fill: #ffc46b; }
.ep-depart .ep-f4 { animation-delay: .5s; }
.ep-depart .ep-f3 { animation-delay: .8s; }
.ep-depart .ep-f2 { animation-delay: 1.1s; }
.ep-depart .ep-f1 { animation-delay: 1.4s; }
.ep-depart .ep-eleve-1 { animation: ep-part-gauche 1.8s ease-in .3s forwards; }
.ep-depart .ep-eleve-2 { animation: ep-part-droite 1.8s ease-in .2s forwards; }
.ep-depart .ep-eleve-3 { animation: ep-part-droite 1.8s ease-in .45s forwards; }
.ep-depart.ep-soir .ep-etoiles { animation: ep-apparait 1.2s ease-in 1.1s forwards; }

/* ---- Contraste : voile fort + ombre douce derrière le texte ---- */
.ep-voile { position: absolute; inset: 0; pointer-events: none; background: rgba(10,32,48,.60); }
.ep-ombre { position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(ellipse 48% 38% at 50% 50%, rgba(6,22,34,.62) 0%, rgba(6,22,34,.38) 55%, rgba(6,22,34,0) 100%); }

/* ---- Texte au centre ---- */
.ep-contenu { position: relative; z-index: 10; text-align: center; padding: 0 20px;
  text-shadow: 0 1px 2px rgba(0,0,0,.45), 0 4px 24px rgba(0,0,0,.35); }
.ep-titre { font-size: clamp(1.25rem, 5cqw, 3.75rem); line-height: 1.1; font-weight: 700; letter-spacing: -0.025em;
  margin: 0 0 clamp(.5rem, 1.6cqw, 1.5rem); }
.ep-ligne { margin: 0 0 clamp(.6rem, 2.2cqw, 2rem); font-size: clamp(.75rem, 2cqw, 1.5rem); font-weight: 400; }
.ep-ligne b { font-weight: 700; }
.ep-heure { display: flex; align-items: center; justify-content: center; gap: clamp(.5rem, 1.2cqw, 1rem); }
.ep-icone { background: rgba(255,255,255,.2); border: 1px solid rgba(255,255,255,.35); padding: clamp(.4rem, 1.1cqw, 1rem);
  border-radius: 9999px; display: flex; }
.ep-icone svg { width: clamp(14px, 2.2cqw, 2rem); height: clamp(14px, 2.2cqw, 2rem); filter: drop-shadow(0 1px 2px rgba(0,0,0,.4)); }
.ep-phrase { margin: 0; font-size: clamp(.85rem, 2.6cqw, 1.875rem); font-weight: 400; }
.ep-chargement { margin-top: clamp(1rem, 3.2cqw, 3rem); }
.ep-piste { width: clamp(6rem, 18cqw, 16rem); height: clamp(2px, .3cqw, .25rem); margin: 0 auto; background: rgba(255,255,255,.28);
  border-radius: 9999px; overflow: hidden; }
.ep-piste i { display: block; height: 100%; width: 0; background: #fff; animation: ep-remplit var(--ep-duree, 3s) cubic-bezier(.42,0,.58,1) forwards; }

.ep-arrivee .ep-bloc, .ep-arrivee .ep-ligne, .ep-arrivee .ep-phrase, .ep-arrivee .ep-chargement { opacity: 0; }
.ep-arrivee .ep-bloc { animation: ep-monte-texte .7s ease-out .5s forwards; }
.ep-arrivee .ep-ligne { animation: ep-monte-texte-10 .7s ease-out .6s forwards; }
.ep-arrivee .ep-phrase { animation: ep-glisse .7s ease-out .8s forwards; }
.ep-arrivee .ep-chargement { animation: ep-apparait .3s ease-out 1s forwards; }
.ep-arrivee .ep-icone { animation: ep-tourne 2s linear infinite; }

.ep-depart .ep-bloc { animation: ep-s-en-va 1.1s ease-in 1.3s forwards; }
.ep-depart .ep-phrase { animation: ep-glisse-sortie 1s ease-in .9s forwards; }
.ep-depart .ep-icone { animation: ep-tourne-inverse 2s ease-in-out forwards; }
.ep-depart .ep-chargement { animation: ep-disparait .4s ease-in 2.1s forwards; }

@keyframes ep-apparait { to { opacity: 1; } }
@keyframes ep-disparait { to { opacity: 0; } }
@keyframes ep-leve { to { transform: translateY(0); } }
@keyframes ep-leve-soir { to { transform: translateY(150px); } }
@keyframes ep-descend { to { transform: translateY(640px); } }
@keyframes ep-tourne { to { transform: rotate(360deg); } }
@keyframes ep-tourne-inverse { to { transform: rotate(-360deg); } }
@keyframes ep-respire { 0%,100% { transform: scale(1); opacity: .28; } 50% { transform: scale(1.12); opacity: .42; } }
@keyframes ep-derive { from { transform: translateX(-420px); } to { transform: translateX(1900px); } }
@keyframes ep-vole { 0% { transform: translate(-200px, 0); opacity: 0; } 8% { opacity: 1; } 60% { transform: translate(1100px, -60px); opacity: 1; } 70%,100% { transform: translate(1300px, -80px); opacity: 0; } }
@keyframes ep-bat { from { transform: scaleY(1); } to { transform: scaleY(.4); } }
@keyframes ep-arrive { to { opacity: 1; transform: translateY(0); } }
@keyframes ep-balance { from { transform: rotate(-1.6deg); } to { transform: rotate(1.6deg); } }
@keyframes ep-flotte { from { transform: skewY(0deg) scaleX(1); } to { transform: skewY(-8deg) scaleX(.9); } }
@keyframes ep-sautille { 0%,70%,100% { transform: translateY(0); } 80% { transform: translateY(-14px); } }
@keyframes ep-allume { to { fill: #ffd976; } }
@keyframes ep-allume-soir { to { fill: #ffc46b; } }
@keyframes ep-eteint { to { fill: #2a4a60; } }
@keyframes ep-part-gauche { to { transform: translateX(-320px); opacity: 0; } }
@keyframes ep-part-droite { to { transform: translateX(340px); opacity: 0; } }
@keyframes ep-monte-texte { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
@keyframes ep-monte-texte-10 { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes ep-glisse { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: none; } }
@keyframes ep-s-en-va { to { opacity: 0; transform: translateY(-20px); } }
@keyframes ep-glisse-sortie { to { opacity: 0; transform: translateX(20px); } }
@keyframes ep-remplit { to { width: 100%; } }
`;

const Morceaux = ({ valeur }) => {
  if (!Array.isArray(valeur)) return valeur ?? null;
  return valeur.map((m, i) => (m.gras ? <b key={i}>{m.texte}</b> : <React.Fragment key={i}>{m.texte}</React.Fragment>));
};

const EcranPassage = ({
  sens = 'arrivee',
  moment = 'jour',
  titre,
  ligne = null,
  phrase,
  icone = 'horloge',
  duree = 3,
  apercu = false
}) => {
  // Identifiants de dégradés propres à chaque écran monté (l'aperçu du CIK et
  // un écran réel ne doivent pas se voler leurs `url(#…)`).
  const id = `ep${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const url = (nom) => `url(#${id}-${nom})`;

  const depart = sens === 'depart';
  const soir = moment === 'soir';
  const cielDebut = depart ? (soir ? 'couchant' : 'jour') : 'aube';
  const cielFin = depart ? (soir ? 'nuit' : 'couchant') : (soir ? 'couchant' : 'jour');
  const Icone = icone === 'sortie' ? LogOut : Clock;
  const aLigne = Array.isArray(ligne) ? ligne.length > 0 : Boolean(ligne);

  return (
    <div
      className={`ep ep-${depart ? 'depart' : 'arrivee'} ep-${soir ? 'soir' : 'jour'} ${apercu ? 'ep-apercu' : 'ep-plein'}`}
      style={{ '--ep-duree': `${duree}s` }}
    >
      <style>{CSS}</style>

      <svg className="ep-scene" viewBox="0 0 1600 1000" preserveAspectRatio="xMidYMax slice" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={`${id}-aube`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2a6f93" /><stop offset=".6" stopColor="#e9a27c" /><stop offset="1" stopColor="#f6d59a" />
          </linearGradient>
          <linearGradient id={`${id}-jour`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#246990" /><stop offset=".55" stopColor="#4fa3cf" /><stop offset="1" stopColor="#bfe6f2" />
          </linearGradient>
          <linearGradient id={`${id}-couchant`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#1b4f6d" /><stop offset=".5" stopColor="#8a5a86" /><stop offset=".85" stopColor="#e98a62" /><stop offset="1" stopColor="#f6c07a" />
          </linearGradient>
          <linearGradient id={`${id}-nuit`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0b1d33" /><stop offset=".6" stopColor="#1b3556" /><stop offset="1" stopColor="#3b4f72" />
          </linearGradient>
        </defs>

        {/* Ciel : un fond de début, un fond de fin qui se fond par-dessus */}
        <rect width="1600" height="1000" fill={url(cielDebut)} />
        <rect className="ep-ciel-fin" width="1600" height="1000" fill={url(cielFin)} />

        {/* Étoiles, au départ du soir */}
        <g className="ep-etoiles" fill="#fff">
          <circle cx="140" cy="90" r="3" /><circle cx="420" cy="160" r="2.5" /><circle cx="700" cy="70" r="3" />
          <circle cx="930" cy="190" r="2" /><circle cx="1110" cy="100" r="2.5" /><circle cx="1500" cy="140" r="3" />
          <circle cx="260" cy="330" r="2" /><circle cx="820" cy="300" r="2.5" /><circle cx="1450" cy="360" r="2" />
        </g>

        {/* Soleil */}
        <g className="ep-soleil">
          <circle className="ep-halo" cx="1340" cy="250" r="190" fill="#f5c542" opacity=".3" />
          <g className="ep-rayons">
            <circle cx="1340" cy="250" r="165" fill="none" stroke="none" />
            <g stroke="#f7d774" strokeWidth="10" strokeLinecap="round" opacity=".85">
              <line x1="1340" y1="95" x2="1340" y2="130" /><line x1="1340" y1="370" x2="1340" y2="405" />
              <line x1="1185" y1="250" x2="1220" y2="250" /><line x1="1460" y1="250" x2="1495" y2="250" />
              <line x1="1230" y1="140" x2="1255" y2="165" /><line x1="1425" y1="335" x2="1450" y2="360" />
              <line x1="1450" y1="140" x2="1425" y2="165" /><line x1="1255" y1="335" x2="1230" y2="360" />
            </g>
          </g>
          <circle className="ep-soleil-coeur" cx="1340" cy="250" r="95" fill="#f5c542" />
          <circle cx="1310" cy="220" r="34" fill="#fff" opacity=".22" />
        </g>

        {/* Nuages */}
        <g fill="#fff">
          <g className="ep-nuage-1" opacity=".9"><ellipse cx="200" cy="200" rx="90" ry="30" /><ellipse cx="250" cy="180" rx="60" ry="38" /><ellipse cx="160" cy="188" rx="45" ry="28" /></g>
          <g className="ep-nuage-2" opacity=".7"><ellipse cx="500" cy="330" rx="70" ry="22" /><ellipse cx="540" cy="314" rx="45" ry="28" /></g>
          <g className="ep-nuage-3" opacity=".8"><ellipse cx="0" cy="120" rx="80" ry="24" /><ellipse cx="45" cy="104" rx="50" ry="32" /></g>
        </g>

        {/* Oiseaux */}
        <g className="ep-oiseaux" fill="none" stroke="#1b4f6d" strokeWidth="4" strokeLinecap="round">
          <path className="ep-aile" d="M300 300q14-14 28 0q14-14 28 0" />
          <path className="ep-aile" d="M360 270q10-10 20 0q10-10 20 0" />
          <path className="ep-aile" d="M250 280q9-9 18 0q9-9 18 0" />
        </g>

        {/* Collines */}
        <path className="ep-monte ep-d1" d="M0 700c220-90 420-90 640-20s420 70 620 0 340-40 340-40v360H0z" fill="#6fc0b3" opacity=".75" />
        <path className="ep-monte ep-d1" d="M0 790c260-60 520-60 800 0s520 50 800-10v220H0z" fill="#45A89F" />

        {/* École — le dessin de la page de connexion */}
        <g className="ep-monte ep-d2">
          <g transform="translate(580 531) scale(1.1)">
            <rect x="0" y="120" width="400" height="220" fill="#f7f5ef" />
            <path d="M-20 125 200 20l220 105z" fill="#C06B58" />
            <rect x="160" y="60" width="80" height="60" fill="#f7f5ef" />
            <circle cx="200" cy="90" r="18" fill="#fff" stroke="#246990" strokeWidth="4" />
            <path d="M200 90v-10M200 90h8" stroke="#246990" strokeWidth="3" strokeLinecap="round" />
            <rect className="ep-fenetre ep-f1" x="30" y="160" width="60" height="50" rx="4" />
            <rect className="ep-fenetre ep-f2" x="120" y="160" width="60" height="50" rx="4" />
            <rect className="ep-fenetre ep-f3" x="220" y="160" width="60" height="50" rx="4" />
            <rect className="ep-fenetre ep-f4" x="310" y="160" width="60" height="50" rx="4" />
            <rect x="165" y="240" width="70" height="100" rx="6" fill="#1b4f6d" />
            <rect x="190" y="-30" width="4" height="60" fill="#1f2937" />
            <path className="ep-drapeau" d="M194-30h46l-10 14 10 14h-46z" fill="#45A89F" />
          </g>
        </g>

        <rect className="ep-monte ep-d1" x="0" y="905" width="1600" height="95" fill="#3f9a8f" />

        {/* Arbres */}
        <g className="ep-monte ep-d3">
          <g className="ep-arbre"><rect x="286" y="800" width="22" height="110" fill="#6b4a2f" /><circle cx="297" cy="760" r="90" fill="#2f8a82" /><circle cx="250" cy="790" r="50" fill="#2f8a82" /></g>
        </g>
        <g className="ep-monte ep-d3">
          <g className="ep-arbre"><rect x="1330" y="820" width="20" height="90" fill="#6b4a2f" /><circle cx="1340" cy="785" r="75" fill="#2f8a82" /><circle cx="1385" cy="810" r="42" fill="#2f8a82" /></g>
        </g>

        {/* Élèves */}
        <g className="ep-monte ep-d4">
          <g className="ep-eleve ep-eleve-1"><circle cx="560" cy="872" r="18" fill="#8d5a3c" /><rect x="544" y="890" width="32" height="50" rx="12" fill="#f0c36b" /></g>
          <g className="ep-eleve ep-eleve-2"><circle cx="1010" cy="878" r="17" fill="#5c3a28" /><rect x="995" y="895" width="30" height="46" rx="11" fill="#C06B58" /></g>
          <g className="ep-eleve ep-eleve-3"><circle cx="1058" cy="872" r="18" fill="#a36b47" /><rect x="1042" y="890" width="32" height="50" rx="12" fill="#fff" /></g>
        </g>
      </svg>

      <div className="ep-voile" />
      <div className="ep-ombre" />

      <div className="ep-contenu">
        <div className="ep-bloc">
          <h1 className="ep-titre"><Morceaux valeur={titre} /></h1>
          {aLigne && <p className="ep-ligne"><Morceaux valeur={ligne} /></p>}
          <div className="ep-heure">
            <div className="ep-icone"><Icone /></div>
            <p className="ep-phrase"><Morceaux valeur={phrase} /></p>
          </div>
        </div>
        <div className="ep-chargement"><div className="ep-piste"><i /></div></div>
      </div>
    </div>
  );
};

export default EcranPassage;
